import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID, Int } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { AdornisEntity } from '@adornis/baseql/entities/adornisEntity.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { abacGuard } from '../abacMiddleware.js';
import { SaraaOrgRole, SaraaUser } from '../db/SaraaUser.js';
import { Users } from '../policies/index.js';
import { PAGE_CHECK } from '../policyHelper.js';
import { loadUserTarget } from '../server/abacTargets.js';
import { resolveCreateUser } from '../server/resolvers/resolveCreateUser.js';
import { resolveGetUserById, resolveSubscribeUsers } from '../server/resolvers/resolveReadUsers.js';
import { resolveSetUserPassword } from '../server/resolvers/resolveSetUserPassword.js';
import { resolveDeleteUser, resolveSetUserArchived, resolveUpdateUser } from '../server/resolvers/resolveWriteUser.js';

@Entity()
export class CreateUserOptions extends AdornisEntity {
  static override _class = 'CreateUserOptions';

  @Field(() => String)
  email: Maybe<string>;

  /** The organization the new user joins. */
  @Field(() => ID)
  organizationId: Maybe<string>;

  /** Their role in that organization. */
  @Field(() => SaraaOrgRole)
  role: Maybe<SaraaOrgRole>;
}

/**
 * Every way an account can be read or written, apart from the membership
 * mutations in `MembershipAPI` — which stay separate on purpose, because editing
 * your own profile is ordinary and granting yourself a membership is escalation.
 *
 * The generic entity mutations are closed for this class, so these are it.
 */

const createUser = registerMutation({
  operationName: 'createUser',
  type: () => String,
  params: [{ type: () => CreateUserOptions, name: 'options' }],
  /**
   * Checked against the membership the new user is about to get: creating an
   * account is only ever allowed as a way of adding someone to an organization
   * the caller administers, so that membership is the thing authority is
   * decided over.
   */
  middlewares: [
    abacGuard(Users, 'create', (args: { options: CreateUserOptions }) => ({
      memberships: [{ organizationId: args.options?.organizationId, role: args.options?.role }],
    })),
  ],
  resolve: resolveCreateUser,
});

const updateUser = registerMutation({
  operationName: 'updateUser',
  type: () => Boolean,
  params: [{ name: 'user', type: () => SaraaUser }],
  middlewares: [abacGuard(Users, 'modify', (args: { user: SaraaUser }) => loadUserTarget(args.user?._id))],
  resolve: resolveUpdateUser,
});

const deleteUser = registerMutation({
  operationName: 'deleteUser',
  type: () => Boolean,
  params: [{ name: 'userId', type: () => ID }],
  /**
   * The guard establishes that the caller administers *some* organization the
   * target belongs to; the resolver additionally requires that they administer
   * *all* of them, and refuses self-deletion. Both halves are needed — see
   * `resolveDeleteUser`.
   */
  middlewares: [abacGuard(Users, 'delete', (args: { userId: string }) => loadUserTarget(args.userId))],
  resolve: resolveDeleteUser,
});

const setUserArchived = registerMutation({
  operationName: 'setUserArchived',
  type: () => Boolean,
  params: [
    { name: 'userId', type: () => ID },
    { name: 'archived', type: () => Boolean },
  ],
  middlewares: [abacGuard(Users, 'delete', (args: { userId: string }) => loadUserTarget(args.userId))],
  resolve: resolveSetUserArchived,
});

/**
 * Sets a user's password — an org admin resetting one for somebody in their
 * organization, and the only way a freshly created account gets one at all.
 *
 * Replaces the engine's `changePassword`, whose resolver asserts the legacy
 * `Users.ChangePassword` permission against `user.roles`. Roles are gone, so that
 * assertion can no longer be satisfied by anybody — see `resolveSetUserPassword`.
 *
 * Guarded by an action of its own rather than by `modify`: setting a password is
 * taking the account, not editing it.
 */
const setUserPassword = registerMutation({
  operationName: 'setUserPassword',
  type: () => Boolean,
  params: [
    { name: 'userId', type: () => ID },
    { name: 'password', type: () => String },
  ],
  middlewares: [abacGuard(Users, 'changePassword', (args: { userId: string }) => loadUserTarget(args.userId))],
  resolve: resolveSetUserPassword,
});

const subscribeUsers = registerSubscription({
  operationName: 'subscribeUsers',
  type: () => [SaraaUser],
  // `(skip, limit, filter)` is the shape the data-view calls a list operation with.
  params: [
    { name: 'skip', type: () => Int },
    { name: 'limit', type: () => Int },
    { name: 'filter', type: () => AdornisFilter },
  ],
  middlewares: [abacGuard(Users, 'view', () => PAGE_CHECK)],
  resolve: resolveSubscribeUsers,
});

const getUserById = registerQuery({
  operationName: 'getUserById',
  type: () => SaraaUser,
  params: [{ name: 'userId', type: () => ID }],
  middlewares: [abacGuard(Users, 'view', (args: { userId: string }) => loadUserTarget(args.userId))],
  resolve: resolveGetUserById,
});

export const UserAPI = {
  create: createUser,
  update: updateUser,
  delete: deleteUser,
  setArchived: setUserArchived,
  setPassword: setUserPassword,
  subscribe: subscribeUsers,
  getById: getUserById,
};
